import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import accountService from '../services/accountService';
import './AccountList.css';

const AccountList = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    parentAccountId: '',
    accountType: '',
    activeOnly: false,
    demoOnly: false,
    limit: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountService.getAllAccounts(filters);
      setAccounts(response.data.accounts || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch accounts (Hotel Chain)');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleApplyFilters = () => {
    fetchAccounts();
  };

  const handleClearFilters = () => {
    setFilters({
      parentAccountId: '',
      accountType: '',
      activeOnly: false,
      demoOnly: false,
      limit: ''
    });
    setTimeout(() => fetchAccounts(), 0);
  };

  const handleDelete = async (id, companyName) => {
    if (window.confirm(`Are you sure you want to delete account (Hotel Chain) "${companyName}"?`)) {
      try {
        await accountService.deleteAccount(id);
        fetchAccounts();
      } catch (err) {
        alert('Failed to delete account (Hotel Chain): ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleView = (id) => {
    navigate(`/accounts/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/accounts/${id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('/accounts/new');
  };

  if (loading) {
    return <div className="loading">Loading accounts (Hotel Chain)...</div>;
  }

  return (
    <div className="account-list-container">
      <div className="account-list-header">
        <h1>Accounts (Hotel Chain)</h1>
        <button className="btn-primary" onClick={handleCreateNew}>
          Create New Account (Hotel Chain)
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="parentAccountId">Parent Account ID</label>
            <input
              type="text"
              id="parentAccountId"
              name="parentAccountId"
              value={filters.parentAccountId}
              onChange={handleFilterChange}
              placeholder="Filter by parent account"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="accountType">Account Type</label>
            <input
              type="text"
              id="accountType"
              name="accountType"
              value={filters.accountType}
              onChange={handleFilterChange}
              placeholder="Filter by type"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="limit">Limit</label>
            <input
              type="number"
              id="limit"
              name="limit"
              value={filters.limit}
              onChange={handleFilterChange}
              placeholder="Max results"
            />
          </div>

          <div className="filter-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="activeOnly"
                checked={filters.activeOnly}
                onChange={handleFilterChange}
              />
              Active Only
            </label>
          </div>

          <div className="filter-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="demoOnly"
                checked={filters.demoOnly}
                onChange={handleFilterChange}
              />
              Demo Only
            </label>
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn-secondary" onClick={handleApplyFilters}>
            Apply Filters
          </button>
          <button className="btn-link" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      <div className="accounts-count">
        Total: {accounts.length} account{accounts.length !== 1 ? 's' : ''}
      </div>

      <div className="table-container">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Company Name</th>
              <th>Username</th>
              <th>Contact Name</th>
              <th>Contact Email</th>
              <th>Country</th>
              <th>Currency</th>
              <th>Region</th>
              <th>Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">No accounts found</td>
              </tr>
            ) : (
              accounts.map(account => (
                <tr
                  key={account.ACCOUNTID}
                  className="clickable-row"
                  onClick={() => handleView(account.ACCOUNTID)}
                  title="Click to view"
                >
                  <td>{account.ACCOUNTID}</td>
                  <td>{account.COMPANYNAME || '-'}</td>
                  <td>{account.USERNAME || '-'}</td>
                  <td>{account.CONTACTNAME || '-'}</td>
                  <td>{account.CONTACTEMAIL || '-'}</td>
                  <td>{account.COUNTRY || '-'}</td>
                  <td>{account.CURRENCY || '-'}</td>
                  <td>{account.AWSREGION || '-'}</td>
                  <td>{account.accounttype || '-'}</td>
                  <td>
                    <span className={`status-badge ${account.ALLOWYN === 'Y' ? 'active' : 'inactive'}`}>
                      {account.ALLOWYN === 'Y' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-action btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(account.ACCOUNTID);
                      }}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(account.ACCOUNTID, account.COMPANYNAME);
                      }}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountList;
